// For new cycle migration Monday 12:00:00 UTC to next Monday 11:59:59 UTC
export const MIGRATION_WEEK_START_DATE = new Date('2025-08-11T00:00:00.000Z');
export const MIGRATION_WEEK_END_DATE = new Date('2025-08-18T11:59:59.999Z');

export function getWeekStartDate(date: Date = new Date()): Date {
  return getPreviousWeekStartDate(0, date);
}

export function getNextWeekStartDate(date: Date = new Date(), weeksForward: number = 1): Date {
  // Truncate fractional weeks to handle cases like 1.7 -> 1
  const weeks = Math.trunc(weeksForward);

  if (weeks === 0) {
    return getWeekStartDate(date);
  }

  // Get the current week start
  const currentWeekStart = getWeekStartDate(date);

  let resultDate = new Date(currentWeekStart.getTime());

  // Handle each week increment, accounting for the special migration week cycle
  for (let i = 0; i < Math.abs(weeks); i++) {
    if (weeks > 0) {
      // Moving forward
      if (resultDate.getTime() === MIGRATION_WEEK_START_DATE.getTime()) {
        // From migration week start, next week is first post-migration week
        // Migration week lasts 7 days + 12 hours, so next week starts at 2025-08-18T12:00:00.000Z
        resultDate = new Date('2025-08-18T12:00:00.000Z');
      } else {
        // Standard 7-day increment for all other weeks
        resultDate = new Date(resultDate.getTime() + 7 * 24 * 60 * 60 * 1000);

        // Apply correct UTC hours based on the target week's position
        if (resultDate.getTime() < MIGRATION_WEEK_START_DATE.getTime()) {
          // Pre-migration: 00:00 UTC
          resultDate.setUTCHours(0, 0, 0, 0);
        } else if (resultDate.getTime() <= MIGRATION_WEEK_END_DATE.getTime()) {
          // Landed in migration week: use migration week start
          resultDate = new Date(MIGRATION_WEEK_START_DATE.getTime());
        } else {
          // Post-migration: 12:00 UTC
          resultDate.setUTCHours(12, 0, 0, 0);
        }
      }
    } else {
      // Moving backward (negative weeks)
      if (resultDate.getTime() === new Date('2025-08-18T12:00:00.000Z').getTime()) {
        // From first post-migration week, previous week is migration week
        resultDate = new Date(MIGRATION_WEEK_START_DATE.getTime());
      } else {
        // Standard 7-day decrement for all other weeks
        resultDate = new Date(resultDate.getTime() - 7 * 24 * 60 * 60 * 1000);

        // Apply correct UTC hours based on the target week's position
        if (resultDate.getTime() < MIGRATION_WEEK_START_DATE.getTime()) {
          // Pre-migration: 00:00 UTC
          resultDate.setUTCHours(0, 0, 0, 0);
        } else if (resultDate.getTime() <= MIGRATION_WEEK_END_DATE.getTime()) {
          // Landed in migration week: use migration week start
          resultDate = new Date(MIGRATION_WEEK_START_DATE.getTime());
        } else {
          // Post-migration: 12:00 UTC
          resultDate.setUTCHours(12, 0, 0, 0);
        }
      }
    }
  }

  return resultDate;
}

export function getPreviousWeekStartDate(weeksAgo: number, date: Date = new Date()): Date {
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date provided');
  }
  if (weeksAgo < 0) {
    throw new Error('weeksAgo must be a non-negative number');
  }

  // for every week prior to migration date (2025-08-18)
  // start date should be monday 00:00:00 UTC
  // end date should be sunday 23:59:59 UTC
  // for every week after migration date
  // start date should be monday 12:00:00 UTC
  // end date should be the following monday 11:59:59 UTC
  // For the week of migration so as to not skip hours
  // start date should be monday 00:00:00 UTC
  // end date should be the following monday 11:59:59 UTC

  // This will ensure backwards compatibility with anything prior to migration date

  const DAYS_IN_WEEK = 7;
  const MS_IN_DAY = 24 * 60 * 60 * 1000;

  const dateWithWeeksAgo = new Date(date.getTime() - weeksAgo * DAYS_IN_WEEK * MS_IN_DAY);

  const currentDay = dateWithWeeksAgo.getUTCDay();

  if (dateWithWeeksAgo.getTime() < MIGRATION_WEEK_START_DATE.getTime()) {
    const diff = currentDay === 0 ? -6 : 1 - currentDay;
    const targetDate = new Date(dateWithWeeksAgo.getTime() + diff * MS_IN_DAY);
    targetDate.setUTCHours(0, 0, 0, 0);
    return targetDate;
  } else if (dateWithWeeksAgo.getTime() <= MIGRATION_WEEK_END_DATE.getTime()) {
    // for migration week, we return the start of the week constant
    return MIGRATION_WEEK_START_DATE;
  } else {
    // Monday 2025-08-25 12:00:00 -> Monday 2025-08-25 12:00:00
    // Tuesday 2025-08-26 -> Monday 2025-08-25 12:00:00
    // Wednesday 2025-08-27 -> Monday 2025-08-25 12:00:00
    // Thursday 2025-08-28 -> Monday 2025-08-25 12:00:00
    // Friday 2025-08-29 -> Monday 2025-08-25 12:00:00
    // Saturday 2025-08-30 -> Monday 2025-08-25 12:00:00
    // Sunday 2025-08-31 -> Monday 2025-08-25 12:00:00
    // Monday 2025-09-01 11:59:59-> Monday 2025-08-25 12:00:00
    let diff = 0;
    if (currentDay === 0) {
      diff = -6;
    } else if (currentDay === 1 && dateWithWeeksAgo.getUTCHours() < 12) {
      diff = -7;
    } else if (currentDay === 1 && dateWithWeeksAgo.getUTCHours() >= 12) {
      diff = 0;
    } else {
      diff = 1 - currentDay;
    }

    const targetDate = new Date(dateWithWeeksAgo.getTime() + diff * MS_IN_DAY);
    targetDate.setUTCHours(12, 0, 0, 0);
    return targetDate;
  }
}

export function getWeekEndDate(date: Date = new Date()): Date {
  const weekStartDate = getWeekStartDate(date);
  if (
    weekStartDate.getTime() < MIGRATION_WEEK_START_DATE.getTime() ||
    weekStartDate.getTime() > MIGRATION_WEEK_END_DATE.getTime()
  ) {
    return new Date(weekStartDate.getTime() + 7 * 24 * 60 * 60 * 1000 - 1);
  } else {
    // for the week of migration, we return the end of the week constant
    return MIGRATION_WEEK_END_DATE;
  }
}

/**
 * Calculates the rakeback end date based on the previous week end date
 * For compatibility with the migration to new cycle for rake share:
 * - If previousWeekEndDate is Sunday (day 0), add 12 hours
 * - If previousWeekEndDate is Monday (day 1), use as-is
 *
 * @param previousWeekEndDate - The end date of the previous week
 * @returns The appropriate rakeback cutoff date
 */
export function calculateRakebackEndDate(previousWeekEndDate: Date): Date {
  // Sunday - Saturday : 0 - 6
  return previousWeekEndDate.getUTCDay() === 0
    ? new Date(previousWeekEndDate.getTime() + 12 * 60 * 60 * 1000)
    : previousWeekEndDate;
}

/**
 * Gets the complete date range and rakeback cutoff for rewards calculation
 * @param previousWeekStartDate - The start date of the previous week
 * @returns Object containing all calculated dates
 */
export function getRewardsDateRange(previousWeekStartDate: Date) {
  const previousWeekEndDate = getWeekEndDate(previousWeekStartDate);
  const rakebackEndDate = calculateRakebackEndDate(previousWeekEndDate);

  return {
    previousWeekStartDate,
    previousWeekEndDate,
    rakebackEndDate,
  };
}
